import React from 'react';
import { Baby, CheckCircle, Calendar, Milk, Moon, Palette } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const ChildHealth = () => {
  const { t } = useLanguage();
  const childInfo = {
    name: 'Chiamaka',
    age: '18 months',
    dob: '2024-07-15',
    gender: 'Female',
  };

  const immunizationSchedule = [
    { age: 'Birth', vaccine: 'BCG, OPV 0, HBV 1', status: 'completed', date: '2024-07-15' },
    { age: '6 weeks', vaccine: 'OPV 1, Penta 1, PCV 1, Rota 1', status: 'completed', date: '2024-08-26' },
    { age: '10 weeks', vaccine: 'OPV 2, Penta 2, PCV 2, Rota 2', status: 'completed', date: '2024-09-23' },
    { age: '14 weeks', vaccine: 'OPV 3, Penta 3, PCV 3, IPV', status: 'completed', date: '2024-10-21' },
    { age: '6 months', vaccine: 'Vitamin A', status: 'completed', date: '2025-01-15' },
    { age: '9 months', vaccine: 'Measles 1, Yellow Fever', status: 'completed', date: '2025-04-15' },
    { age: '18 months', vaccine: 'Measles 2, DPT Booster', status: 'upcoming', date: '2026-03-20' },
  ];

  const growthData = [
    { month: '0', weight: 3.2, height: 48 },
    { month: '3', weight: 5.8, height: 58 },
    { month: '6', weight: 7.5, height: 65 },
    { month: '9', weight: 8.8, height: 70 },
    { month: '12', weight: 9.8, height: 74 },
    { month: '15', weight: 10.5, height: 77 },
    { month: '18', weight: 11.2, height: 80 },
  ];

  const milestones = [
    { age: '6 months', milestone: 'Sits without support', achieved: true },
    { age: '9 months', milestone: 'Crawls', achieved: true },
    { age: '12 months', milestone: 'First steps', achieved: true },
    { age: '15 months', milestone: 'Says 2-3 words', achieved: true },
    { age: '18 months', milestone: 'Runs, feeds self', achieved: false },
    { age: '24 months', milestone: 'Combines 2 words', achieved: false },
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4 mb-2">
            <div className="p-2 md:p-3 bg-gradient-to-br from-pink-500 to-pink-500 rounded-xl md:rounded-2xl shadow-lg shadow-pink-500/30">
              <Baby className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                {t('childHealth')}
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">{t('trackChildHealthSubtitle')}</p>
            </div>
          </div>
        </div>

        {/* Child Info */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-6 md:mb-8 hover:shadow-xl transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">{childInfo.name}</h2>
              <div className="space-y-1.5">
                <p className="text-sm md:text-base text-gray-600">{t('age')}: <span className="font-semibold">{childInfo.age}</span></p>
                <p className="text-sm md:text-base text-gray-600">{t('dateOfBirth')}: <span className="font-semibold">{new Date(childInfo.dob).toLocaleDateString()}</span></p>
                <p className="text-sm md:text-base text-gray-600">{t('gender')}: <span className="font-semibold">{childInfo.gender}</span></p>
              </div>
            </div>
            <div className="p-3 md:p-4 bg-gradient-to-br from-pink-500 to-pink-500 rounded-xl md:rounded-2xl shadow-lg shadow-pink-500/30">
              <Baby className="w-10 h-10 md:w-16 md:h-16 text-white" />
            </div>
          </div>
        </div>

        {/* Immunization Schedule */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-6 md:mb-8">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">{t('immunizationScheduleTitle')}</h2>
          <div className="space-y-3 md:space-y-4">
            {immunizationSchedule.map((item, index) => (
              <div key={index} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 md:p-4 rounded-xl transition-all ${
                item.status === 'completed' 
                  ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200' 
                  : 'bg-gradient-to-r from-emerald-50 to-sky-50 border-2 border-blue-200'
              }`}>
                <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${
                    item.status === 'completed' ? 'bg-emerald-500' : 'bg-emerald-500'
                  }`}>
                    {item.status === 'completed' ? 
                      <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-white" /> :
                      <Calendar className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    }
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-sm md:text-base text-gray-900">{item.age}</h3>
                    <p className="text-xs md:text-sm text-gray-600 mt-0.5">{item.vaccine}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.date}</p>
                  </div>
                </div>
                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap self-start sm:self-auto ${
                  item.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* Growth Chart */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">{t('growthChartTitle')}</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-sm md:text-base text-gray-700 mb-3">{t('weight')} (kg)</h3>
                <div className="space-y-3">
                  {growthData.slice(-4).map((data, index) => (
                    <div key={index} className="flex items-center gap-2 md:gap-3">
                      <span className="w-12 md:w-16 text-xs md:text-sm text-gray-600 font-medium">{data.month} mo</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-7 md:h-8 overflow-hidden shadow-inner">
                        <div 
                          className="bg-gradient-to-r from-pink-500 to-pink-500 h-full flex items-center justify-end px-2 md:px-3 transition-all"
                          style={{ width: `${(data.weight / 15) * 100}%` }}
                        >
                          <span className="text-xs md:text-sm text-white font-bold">{data.weight} kg</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t-2 border-gray-200">
                <h3 className="font-semibold text-sm md:text-base text-gray-700 mb-3">{t('height')} (cm)</h3>
                <div className="space-y-3">
                  {growthData.slice(-4).map((data, index) => (
                    <div key={index} className="flex items-center gap-2 md:gap-3">
                      <span className="w-12 md:w-16 text-xs md:text-sm text-gray-600 font-medium">{data.month} mo</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-7 md:h-8 overflow-hidden shadow-inner">
                        <div 
                          className="bg-gradient-to-r from-emerald-500 to-green-500 h-full flex items-center justify-end px-2 md:px-3 transition-all"
                          style={{ width: `${(data.height / 100) * 100}%` }}
                        >
                          <span className="text-xs md:text-sm text-white font-bold">{data.height} cm</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Developmental Milestones */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">{t('developmentalMilestonesTitle')}</h2>
            <div className="space-y-3">
              {milestones.map((item, index) => (
                <div key={index} className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                  item.achieved 
                    ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200' 
                    : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm text-gray-600 font-medium">{item.age}</p>
                      <h4 className="font-semibold text-sm md:text-base text-gray-900 mt-0.5">{item.milestone}</h4>
                    </div>
                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${
                      item.achieved ? 'bg-emerald-500' : 'bg-gray-300'
                    }`}>
                      {item.achieved && <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-white" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Health Tips */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">{t('healthTipsForAge')} {childInfo.age}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 md:p-5 bg-gradient-to-br from-pink-50 to-pink-50 rounded-xl border-2 border-pink-200 hover:shadow-lg transition-all">
              <div className="p-2 bg-pink-500 rounded-lg w-fit shadow-md mb-3">
                <Milk className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h4 className="font-bold text-sm md:text-base text-gray-900 mb-1.5">{t('nutrition')}</h4>
              <p className="text-xs md:text-sm text-gray-600">{t('provideBalancedMeals')}</p>
            </div>
            <div className="p-4 md:p-5 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border-2 border-emerald-200 hover:shadow-lg transition-all">
              <div className="p-2 bg-emerald-500 rounded-lg w-fit shadow-md mb-3">
                <Moon className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h4 className="font-bold text-sm md:text-base text-gray-900 mb-1.5">{t('sleep')}</h4>
              <p className="text-xs md:text-sm text-gray-600">{t('hoursOfSleep')}</p>
            </div>
            <div className="p-4 md:p-5 bg-gradient-to-br from-emerald-50 to-sky-50 rounded-xl border-2 border-blue-200 hover:shadow-lg transition-all sm:col-span-2 lg:col-span-1">
              <div className="p-2 bg-emerald-500 rounded-lg w-fit shadow-md mb-3">
                <Palette className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <h4 className="font-bold text-sm md:text-base text-gray-900 mb-1.5">{t('playAndLearning')}</h4>
              <p className="text-xs md:text-sm text-gray-600">{t('encouragePlay')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildHealth;
